import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { KanbanFieldMapping, WritebackConfig } from '@/types/pm-types';
import { useDataWriteback } from '@/hooks/useDataWriteback';
import type { ComponentDataWriteOperation } from '@/types/componentDataBinding';
import { pmTheme } from './pmTheme';

export interface KanbanCardDetailModalProps {
  record: Record<string, unknown>;
  fieldMapping: KanbanFieldMapping;
  writeback?: WritebackConfig;
  writeTarget?: unknown;
  requireExplicitWriteTarget?: boolean;
  writePermissions?: Partial<Record<ComponentDataWriteOperation, boolean>>;
  writeTargetRequestingOrganizationId?: string | null;
  componentId: string;
  onClose: () => void;
}

const FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  column: 'Status',
  description: 'Description',
  assignee: 'Assignee',
  priority: 'Priority',
  dueDate: 'Due Date',
  tags: 'Tags',
  sortOrder: 'Order',
  image: 'Image',
};

export const KanbanCardDetailModal: React.FC<KanbanCardDetailModalProps> = ({
  record,
  fieldMapping,
  writeback,
  writeTarget,
  requireExplicitWriteTarget = false,
  writePermissions,
  writeTargetRequestingOrganizationId,
  componentId,
  onClose,
}) => {
  const {
    updateRecord,
    isUpdating,
    error: writebackError,
    writeTargetResolution,
  } = useDataWriteback({
    writebackConfig: writeback,
    writeTarget,
    requireExplicitWriteTarget,
    writePermissions,
    writeTargetRequestingOrganizationId,
    componentId,
  });

  const recordId = String(
    record[fieldMapping.id || '_id'] ?? record['_id'] ?? record['id'] ?? ''
  );

  const isEditable = writeTargetResolution.status === 'ready' && !!recordId;

  // Local edit state per field
  const [editValues, setEditValues] = useState<Record<string, string>>({});

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleFieldChange = useCallback((dataField: string, value: string) => {
    setEditValues(prev => ({ ...prev, [dataField]: value }));
  }, []);

  const handleFieldBlur = useCallback(async (dataField: string) => {
    const newValue = editValues[dataField];
    if (newValue === undefined) return;

    const originalValue = String(record[dataField] ?? '');
    if (newValue === originalValue) return;

    await updateRecord(recordId, { [dataField]: newValue });
  }, [editValues, record, recordId, updateRecord]);

  const handleFieldKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.currentTarget instanceof HTMLElement) {
      e.currentTarget.blur();
    }
  }, []);

  // Build visible fields from fieldMapping (skip 'id')
  const fields = Object.entries(fieldMapping)
    .filter((entry): entry is [string, string] => entry[0] !== 'id' && entry[1] != null)
    .map(([role, dataField]) => ({
      role,
      dataField,
      label: FIELD_LABELS[role] || role,
      value: String(record[dataField] ?? ''),
    }));

  const content = (
    <div
      data-testid="kanban-card-detail-backdrop"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
      }}
    >
      <div
        data-testid="kanban-card-detail-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="kanban-detail-title"
        onClick={e => e.stopPropagation()}
        style={{
          background: pmTheme.surface,
          color: pmTheme.text,
          borderRadius: 12,
          padding: 24,
          maxWidth: 480,
          width: '90%',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: 'var(--shadow-xl)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 id="kanban-detail-title" style={{ margin: 0, fontSize: 16, fontWeight: 600, color: pmTheme.text }}>Card Details</h3>
          <button
            data-testid="kanban-card-detail-close"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 18,
              color: pmTheme.textSubtle,
              padding: '4px 8px',
              borderRadius: 4,
            }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Fields */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {fields.map(({ role, dataField, label, value }) => (
            <div
              key={role}
              data-testid={`kanban-card-detail-field-${role}`}
              style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
            >
              <label style={{ fontSize: 11, fontWeight: 600, color: pmTheme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {label}
              </label>
              {isEditable ? (
                role === 'description' ? (
                  <textarea
                    value={editValues[dataField] ?? value}
                    onChange={e => handleFieldChange(dataField, e.target.value)}
                    onBlur={() => handleFieldBlur(dataField)}
                    onKeyDown={handleFieldKeyDown}
                    rows={3}
                    style={{
                      fontSize: 13,
                      color: pmTheme.text,
                      background: pmTheme.surface,
                      border: `1px solid ${pmTheme.input}`,
                      borderRadius: 6,
                      padding: '6px 10px',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                    }}
                  />
                ) : (
                  <input
                    type="text"
                    value={editValues[dataField] ?? value}
                    onChange={e => handleFieldChange(dataField, e.target.value)}
                    onBlur={() => handleFieldBlur(dataField)}
                    onKeyDown={handleFieldKeyDown}
                    style={{
                      fontSize: 13,
                      color: pmTheme.text,
                      background: pmTheme.surface,
                      border: `1px solid ${pmTheme.input}`,
                      borderRadius: 6,
                      padding: '6px 10px',
                    }}
                  />
                )
              ) : (
                <span style={{ fontSize: 13, color: pmTheme.text }}>
                  {value || '—'}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Status indicators */}
        {isUpdating && (
          <div style={{ marginTop: 12, fontSize: 12, color: pmTheme.textMuted }}>Saving...</div>
        )}
        {writebackError && (
          <div style={{ marginTop: 12, fontSize: 12, color: pmTheme.danger }}>{writebackError}</div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};

KanbanCardDetailModal.displayName = 'KanbanCardDetailModal';
